import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import SectionEyebrow from "@/components/common/SectionEyebrow";
import { publicApi } from "@/lib/api";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowRight, Filter } from "lucide-react";

export default function ResearchCompendium() {
  const [domains, setDomains] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [params, setParams] = useSearchParams();
  const active = params.get("domain") || "";

  useEffect(() => {
    publicApi.domains().then(setDomains).catch(() => {});
    publicApi.questions().then(setQuestions).catch(() => {});
  }, []);

  const filtered = useMemo(
    () => (active ? questions.filter((q) => q.domain_slug === active) : questions),
    [questions, active]
  );

  return (
    <PublicLayout title="Research Compendium">
      <section className="relative bsca-dark bsca-cosmic bsca-grain -mt-16 md:-mt-20 lg:-mt-[88px] pt-36 md:pt-44 pb-24 overflow-hidden">
        <div className="bsca-field-lines" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-10 lg:px-14 bsca-fade-up">
          <SectionEyebrow dark>Research Compendium</SectionEyebrow>
          <h1 className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-3xl">
            Frontier questions, treated with <span className="text-[#D4AF37]">rigor and care.</span>
          </h1>
          <p className="mt-6 text-white/75 text-lg max-w-2xl">
            Each card frames a major open question — what is known, what remains unknown, candidate methods, and how to collaborate.
          </p>
        </div>
      </section>

      <section className="bg-[#F8F6F1] py-12 sticky top-16 md:top-20 z-30 border-b border-black/5">
        <div className="max-w-7xl mx-auto px-6 md:px-10 lg:px-14 flex items-center gap-3 overflow-x-auto">
          <Filter className="h-4 w-4 text-[#7A5A06]" />
          <button
            data-testid="filter-domain-all"
            onClick={() => setParams({})}
            className={`text-sm px-3 py-2 ${!active ? "bsca-tab-active" : "text-[#4B5563] hover:text-[#050B14]"}`}
          >
            All
          </button>
          {domains.map((d) => (
            <button
              key={d.slug}
              data-testid={`filter-domain-${d.slug}`}
              onClick={() => setParams({ domain: d.slug })}
              className={`text-sm px-3 py-2 whitespace-nowrap ${
                active === d.slug ? "bsca-tab-active" : "text-[#4B5563] hover:text-[#050B14]"
              }`}
            >
              {d.title}
            </button>
          ))}
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <Accordion type="single" collapsible className="border-y border-black/10">
            {filtered.length === 0 && (
              <div className="py-16 text-center text-[#4B5563]">No questions in this domain yet.</div>
            )}
            {filtered.map((q, i) => (
              <AccordionItem key={q.id || i} value={q.id || `q-${i}`} className="border-b border-black/10">
                <AccordionTrigger
                  data-testid={`question-trigger-${i}`}
                  className="hover:no-underline py-8 text-left"
                >
                  <div className="flex flex-col md:flex-row md:items-center gap-3 w-full pr-4">
                    <span className="bsca-eyebrow text-[#7A5A06] text-[10px] md:w-24">
                      {(domains.find((d) => d.slug === q.domain_slug)?.title || q.domain_slug || "—").split(" ")[0]}
                    </span>
                    <span className="font-serif text-xl md:text-2xl tracking-tight leading-snug">
                      {q.title}
                    </span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pb-10">
                  <div className="grid md:grid-cols-2 gap-x-12 gap-y-8 text-[#1f2937]">
                    <div>
                      <div className="bsca-eyebrow text-[#7A5A06] text-[10px]">Known</div>
                      <p className="mt-2 leading-relaxed">{q.known}</p>
                    </div>
                    <div>
                      <div className="bsca-eyebrow text-[#7A5A06] text-[10px]">Unknown</div>
                      <p className="mt-2 leading-relaxed">{q.unknown}</p>
                    </div>
                    <div>
                      <div className="bsca-eyebrow text-[#7A5A06] text-[10px]">Research Challenge</div>
                      <p className="mt-2 leading-relaxed">{q.challenge}</p>
                    </div>
                    <div>
                      <div className="bsca-eyebrow text-[#7A5A06] text-[10px]">Metrics</div>
                      <p className="mt-2 leading-relaxed">{q.metrics}</p>
                    </div>
                    <div>
                      <div className="bsca-eyebrow text-[#7A5A06] text-[10px]">Suggested Methods</div>
                      <ul className="mt-2 space-y-1 text-[#4B5563]">
                        {(q.methods || []).map((m) => (
                          <li key={m}>· {m}</li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <div className="bsca-eyebrow text-[#7A5A06] text-[10px]">Potential Collaborators</div>
                      <ul className="mt-2 space-y-1 text-[#4B5563]">
                        {(q.collaborators || []).map((m) => (
                          <li key={m}>· {m}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <Link
                    to="/engage#collaboration"
                    data-testid={`collaborate-${i}`}
                    className="mt-8 inline-flex items-center bg-[#050B14] text-white px-6 py-3 rounded-sm bsca-lift"
                  >
                    Collaborate on this Question <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="bsca-dark bsca-cosmic bsca-grain py-24 relative overflow-hidden">
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 text-center">
          <SectionEyebrow dark>Collaborate</SectionEyebrow>
          <h2 className="font-serif text-4xl md:text-5xl text-white tracking-tight">Bring your inquiry to the alliance.</h2>
          <Link to="/engage#collaboration" data-testid="btn-compendium-cta" className="mt-10 inline-flex items-center bg-[#D4AF37] text-[#050B14] font-medium px-8 py-4 rounded-sm bsca-lift">
            Propose research <ArrowRight className="h-4 w-4 ml-2" />
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
